import React, { useState, useEffect, useRef } from "react";
import { Button, Icon, Popover, Position, PopoverInteractionKind, Dialog } from "@blueprintjs/core";
import EmptyStateNotice from "../goal_performance/EmptyStateNotice";
import { FilledButton, TextButton } from "../buttons-20250224T111359Z-001/buttons";
import ToastStore from "../../stores/ToastStore";
import axios from 'axios';
import Cookies from 'js-cookie';
import QuestionDeleteConfirmation from "./QuestionDeleteConfirmation";
import ConfirmationDialog from "../../views/ConfirmationDialog";
import EditIcon from "../../assets/editIcon.svg";
import Delete from "../../assets/Delete.svg";
import Flexbox from "flexbox-react";



const QuestionListPanel = ({
    goalCycleId,
    questionGroups = null,
    jobProfileList = [],
    selectedJobProfileFilter,
    setSelectedJobProfileFilter,
    isWeightAllow,
    onEditQuestion,
    onDeleteQuestion,
    onCreateRemark,
    onUpdateRemark,
    onDeleteRemark,
    onRefresh,
    isQuestionUploading,
    isQuestionDownloading,
    onImport,
    onReorderQuestion,
    onMoveQuestion,
}) => {


    const [questionToDelete, setQuestionToDelete] = useState(null);
    const remarkSavePending = useRef(false);
    const [isRemarkDialogOpen, setIsRemarkDialogOpen] = useState(false);
    const [remarkQuestionTitle, setRemarkQuestionTitle] = useState("");
    const [remarkQuestionCategoryId, setRemarkQuestionCategoryId] = useState(null);
    const [remarkEditingId, setRemarkEditingId] = useState(null);
    const [remarkToDelete, setRemarkToDelete] = useState(null);
    const [isDeleteRemarkSubmitting, setIsDeleteRemarkSubmitting] = useState(false);
    const [deleteRemarkError, setDeleteRemarkError] = useState("");
    const deleteRemarkPending = useRef(false);
    const [isRemarkSubmitting, setIsRemarkSubmitting] = useState(false);
    const [isDeleteRemarkDialogOpen, setIsDeleteRemarkDialogOpen] = useState(false);

    const sortedQuestionGroups = questionGroups ? [...questionGroups] : [];
    const totalWeight = sortedQuestionGroups?.reduce((sum, group) => {
        return sum + group?.questions?.reduce((s, q) => s + (q?.weightage || 0), 0);
    }, 0);

    const openAddRemarkDialog = (categoryId) => {
        setRemarkEditingId(null);
        setRemarkQuestionTitle("");
        setRemarkQuestionCategoryId(categoryId);
        setIsRemarkDialogOpen(true);
    };

    const handleDragStart = (e, q, fromGroup) => {
        e.dataTransfer.setData('text/plain', JSON.stringify({ questionId: q.id, fromCategoryId: fromGroup?.category_id, questionName: q.question }));
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOverGroup = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        try {
            document.body.classList.add('drag-drop-pointer');
        } catch (err) {
        }
    };

    const handleDragLeaveGroup = (e) => {
        try {
            document.body.classList.remove('drag-drop-pointer');
        } catch (err) {
            // ignore
        }
    };

    const handleDragEnd = (e) => {
        try {
            document.body.classList.remove('drag-drop-pointer');
        } catch (err) {
        }
    };

    // ensure CSS for drag pointer exists once
    useEffect(() => {
        try {
            const styleId = 'question-list-drag-style';
            if (!document.getElementById(styleId)) {
                const style = document.createElement('style');
                style.id = styleId;
                style.innerHTML = '.drag-drop-pointer, .drag-drop-pointer * { cursor: pointer !important; }';
                document.head.appendChild(style);
            }
        } catch (err) {
        }
    }, []);

    const callUpdateApi = async (questionId, data) => {
        const baseUrl = Cookies.get('apiEnv');
        const url = `${baseUrl ? baseUrl.replace(/\/+$/, '') : ''}/review_questions/${questionId}`;
        return axios.put(url, { ...data, goal_setting_id: goalCycleId });
    };

    const handleDropOnGroup = async (e, toGroup, insertBeforeQuestionId = null) => {
        e.preventDefault();
        e.stopPropagation();
        try {
            const payload = e.dataTransfer.getData('text/plain');
            if (!payload) return;
            const { questionId, fromCategoryId, questionName } = JSON.parse(payload);

            let position = 1;
            if (toGroup?.questions && toGroup.questions.length > 0) {
                if (insertBeforeQuestionId) {
                    const idx = toGroup.questions.findIndex(q => q.id === insertBeforeQuestionId);
                    position = idx >= 0 ? toGroup.questions[idx].position : toGroup.questions.length + 1;
                } else {
                    position = toGroup.questions.length + 1;
                }
            }

            if (fromCategoryId === toGroup?.category_id) {
                await callUpdateApi(questionId, { position });
                if (typeof onReorderQuestion === 'function') {
                    await onReorderQuestion(questionId, toGroup?.category_id, position);
                }
            } else {
                const apiPayload = { question_id: questionId, question_category_id: toGroup?.category_id, position };
                await callUpdateApi(questionId, apiPayload);
                if (typeof onMoveQuestion === 'function') {
                    await onMoveQuestion(questionId, fromCategoryId, toGroup?.category_id, toGroup?.category_name || 'Uncategorized', position, questionName);
                }
            }

            await onRefresh?.();
        } catch (err) {
            console.error('Error handling drop', err);
            ToastStore.showToast(err?.response?.data?.message || 'Failed to move question', 'danger');
        } finally {
            try {
                document.body.classList.remove('drag-drop-pointer');
            } catch (err) {
            }
        }
    };

    const openEditRemarkDialog = (remark, group) => {
        setRemarkEditingId(remark.id);
        setRemarkQuestionTitle(remark.question || "");
        setRemarkQuestionCategoryId(remark.question_category_id || group?.category_id);
        setIsRemarkDialogOpen(true);
    };

    const handleSaveRemark = async () => {
        if (remarkSavePending.current) return;
        if (!remarkQuestionTitle.trim()) {
            ToastStore.showToast("Comment is required", "warning");
            return;
        }
        remarkSavePending.current = true;
        setIsRemarkSubmitting(true);
        try {
            if (remarkEditingId) {
                await onUpdateRemark(remarkEditingId, {
                    question_category_id: remarkQuestionCategoryId,
                    question: remarkQuestionTitle.trim()
                });
                ToastStore.showToast("Comment updated successfully", "success");
            } else {
                await onCreateRemark({
                    question_category_id: remarkQuestionCategoryId,
                    question: remarkQuestionTitle.trim()
                });
                ToastStore.showToast("Comment created successfully", "success");
            }
        } catch (error) {
            console.error("Error saving comment", error);
            ToastStore.showToast(
                error?.response?.data?.error || error?.response?.data?.message || "Failed to save comment",
                "danger"
            );
            return;
        } finally {
            remarkSavePending.current = false;
            setIsRemarkSubmitting(false);
        }
        setIsRemarkDialogOpen(false);
        try {
            await onRefresh();
        } catch (error) {
            ToastStore.showToast("Comment saved. Reload the page to see the latest list.", "warning");
        }
    };

    const openDeleteRemarkDialog = (remark) => {
        setRemarkToDelete(remark);
        setDeleteRemarkError("");
        setIsDeleteRemarkDialogOpen(true);
    };

    const closeDeleteRemarkDialog = () => {
        if (deleteRemarkPending.current) return;
        setIsDeleteRemarkDialogOpen(false);
        setRemarkToDelete(null);
        setDeleteRemarkError("");
    };

    const handleDeleteRemark = async () => {
        if (!remarkToDelete || deleteRemarkPending.current) return;
        deleteRemarkPending.current = true;
        setIsDeleteRemarkSubmitting(true);
        setDeleteRemarkError("");
        try {
            await onDeleteRemark(remarkToDelete.id);
        } catch (error) {
            console.error("Error deleting comment", error);
            const message = error?.response?.data?.error || error?.response?.data?.message || "Failed to delete comment";
            setDeleteRemarkError(String(message));
            ToastStore.showToast(String(message), "danger");
            return;
        } finally {
            deleteRemarkPending.current = false;
            setIsDeleteRemarkSubmitting(false);
        }
        setIsDeleteRemarkDialogOpen(false);
        setRemarkToDelete(null);
        ToastStore.showToast("Comment deleted successfully", "success");
        try {
            await onRefresh();
        } catch (error) {
            ToastStore.showToast("Comment deleted. Reload the page to see the latest list.", "warning");
        }
    };

    const handleCancelRemark = () => {
        if (remarkSavePending.current) return;
        setIsRemarkDialogOpen(false);
        setRemarkQuestionTitle("");
        setRemarkQuestionCategoryId(null);
        setRemarkEditingId(null);
    };

    const handleQuestionUploadClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        questionFileInputRef.current?.click();
    };

    const handleQuestionFileChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const allowedExtensions = ["xls", "xlsx", "xlsm", "xlsb", "xlt", "xltx", "ods", "csv"];
        const fileExt = file.name.split(".").pop().toLowerCase();

        if (!allowedExtensions.includes(fileExt)) {
            ToastStore.showToast("Invalid file format", "danger");
            questionFileInputRef.current.value = "";
            return;
        }

        await onImport?.upload({
            file,
            category: selectedCategory
        });
        questionFileInputRef.current.value = "";
    };


    return (
        <div className="question-list-panel">

            {/* HEADER */}


            <div
                className="question-list-header"
            >
                <h3 className="question-list-title">Questions List</h3>
                {/* IMPORT BUTTON */}
                {/* <div>
                <Popover
                    position={Position.BOTTOM_RIGHT}
                    interactionKind={PopoverInteractionKind.CLICK}
                    portalContainer={document.body}
                    content={
                        <div className="import-popover">

                            <div
                                className="import-item"
                                style={{ cursor: isQuestionDownloading ? "not-allowed" : "pointer", opacity: isQuestionDownloading ? 0.6 : 1 }}
                                onClick={!isQuestionDownloading ? onImport?.downloadSample : undefined}
                            >
                                <div style={{ background: "#e0edff", color: "#2d72d9", borderRadius: "8px", padding: "8px", display: "flex" }}>
                                    <Icon icon="download" />
                                </div>
                                <div>
                                    <div className="import-title">Download Sample</div>
                                    <div className="import-description"> Download a sample file for the import format</div>
                                </div>

                            </div>

                            <div
                                className="import-item import-upload"
                                style={{ cursor: isQuestionUploading ? "not-allowed" : "pointer", opacity: isQuestionUploading ? 0.6 : 1 }}
                                onClick={!isQuestionUploading ? handleQuestionUploadClick : undefined}
                            >
                                <div style={{ background: "#e0edff", color: "#2d72d9", borderRadius: "8px", padding: "8px", display: "flex" }}>
                                    <Icon icon="upload" />
                                </div>
                                <div>
                                    <div className="import-title">Upload File</div>
                                    <div className="import-description">Import questions</div>
                                </div>
                            </div>

                        </div>
                    }
                >
                    <Button
                        icon="import"
                        rightIcon="caret-down"
                        text="Impor Quesationst"
                        disabled={isQuestionUploading || isQuestionDownloading}
                        style={{
                            borderRadius: "10px",
                            color: "#2d72d9",
                            // backgroundColor: "rgba(45, 114, 217, 0.08)",
                            border: "1px solid rgba(45, 114, 217, 0.3)",
                            padding: "9px 18px",
                            fontWeight: 500,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "8px",
                        }}
                    />
                </Popover>
                <input
                    type="file"
                    ref={questionFileInputRef}
                    style={{ display: "none" }}
                    accept=".xls,.xlsx,.xlsm,.xlsb,.xlt,.xltx,.ods,.csv"
                    onChange={handleQuestionFileChange}
                />
                </div> */}

                {jobProfileList?.length > 0 && (
                    <select
                        value={selectedJobProfileFilter}
                        onChange={(e) =>
                            setSelectedJobProfileFilter(e.target.value)
                        }
                        style={{
                            border: "1px solid #d9d9d9",
                            borderRadius: "8px",
                            padding: "6px 10px",
                            minWidth: "220px"
                        }}
                    >
                        <option value="all">All </option>
                        {jobProfileList.map(job => (
                            <option key={job.id} value={job.id}>
                                {job.name}
                            </option>
                        ))}
                    </select>
                )}
            </div>

            {/* TOTAL WEIGHT */}
            {isWeightAllow && (
                <>
                    <div
                        style={{
                            fontWeight: 600,
                            color: totalWeight > 100 ? "red" : "green",
                            marginBottom: 10
                        }}
                    >
                        Total Weightage: {totalWeight}
                    </div>

                    {totalWeight > 100 && (
                        <div style={{ color: "red", fontSize: 13, marginBottom: 10 }}>
                            ⚠️ Total Weightage should be 100 for correct calculation.
                        </div>
                    )}
                </>
            )}

            {/* EMPTY STATE */}
            {questionGroups?.length === 0 ? (
                <div className="question-empty">
                    <EmptyStateNotice
                        title="No Questions Added"
                        description="Start by adding your first question"
                    />
                </div>
            ) : (

                <div className="question-list-scroll">
                    {sortedQuestionGroups?.length > 0 &&
                        sortedQuestionGroups.map((group, index) => (
                            <div key={index} className="question-group" onDragOver={handleDragOverGroup} onDragLeave={handleDragLeaveGroup} onDrop={(e) => handleDropOnGroup(e, group)}>

                                {/* CATEGORY HEADER */}
                                <div className="question-group-header">

                                    <div className="question-group-title">
                                        {group?.category_name || "Uncategorized"}
                                        <span className="question-group-count">
                                            ({group?.questions?.length || 0})
                                        </span>
                                    </div>

                                    {group?.category_id && (
                                        <TextButton
                                            text="+ Add Comment"
                                            onClick={() => openAddRemarkDialog(group?.category_id)}
                                        />
                                    )}
                                </div>

                                {/* REMARKS */}
                                {group?.comment_questions?.length > 0 && (
                                    <div>
                                        {group?.comment_questions?.length > 0 && group?.comment_questions?.map((remark) => (
                                            <div key={remark.id} className="remark-item">

                                                <div className="remark-text">
                                                    <strong>Comment:</strong> {remark.question}
                                                </div>

                                                <div className="remark-actions">
                                                    <Button
                                                        icon={<img src={EditIcon} style={{ width: 18, height: 18 }} />}
                                                        minimal
                                                        onClick={(e) => { openEditRemarkDialog(remark, group); }}
                                                    />
                                                    <Button
                                                        icon={<img src={Delete} style={{ width: 18, height: 18 }} />}
                                                        minimal
                                                        onClick={() => openDeleteRemarkDialog(remark)}
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* QUESTIONS */}
                                {group?.questions?.length > 0 && group?.questions?.map((q) => (
                                        <div key={q.id} className="question-card" draggable onDragStart={(e) => handleDragStart(e, q, group)} onDragEnd={handleDragEnd} onDragOver={(e) => e.preventDefault()} onDrop={(e) => handleDropOnGroup(e, group, q.id)}>
                                            <div className="question-card-header">

                                                {/* DRAG HANDLE (9 dots) */}
                                                <div
                                                    className="question-drag-handle"
                                                    title="Drag to move or reorder question"
                                                    style={{
                                                        display: "inline-flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        width: 22,
                                                        height: 22,
                                                        marginRight: 8,
                                                        cursor: "grab",
                                                        flex: "0 0 auto"
                                                    }}
                                                >
                                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <g fill="#8a8f98">
                                                            <circle cx="3" cy="3" r="1.2" />
                                                            <circle cx="8" cy="3" r="1.2" />
                                                            <circle cx="13" cy="3" r="1.2" />

                                                            <circle cx="3" cy="8" r="1.2" />
                                                            <circle cx="8" cy="8" r="1.2" />
                                                            <circle cx="13" cy="8" r="1.2" />

                                                            <circle cx="3" cy="13" r="1.2" />
                                                            <circle cx="8" cy="13" r="1.2" />
                                                            <circle cx="13" cy="13" r="1.2" />
                                                        </g>
                                                    </svg>
                                                </div>

                                                {/* LEFT */}
                                                <div
                                                    style={{ flex: 1, cursor: "pointer" }}
                                                    onClick={() => onEditQuestion(q, group)}
                                                >
                                                    <div className="question-text">{q.question}</div>

                                                    {isWeightAllow && (
                                                        <div className="question-weight">
                                                            Weight: {q.weightage || 0}
                                                        </div>
                                                    )}
                                                </div>

                                                {/* ACTIONS */}
                                                <div className="question-actions">
                                                    <Button
                                                        icon={<img src={EditIcon} style={{ width: 18, height: 18 }} />}
                                                        minimal
                                                        onClick={(e) => { onEditQuestion(q, group); }}
                                                    />
                                                    <Button
                                                        icon={<img src={Delete} style={{ width: 18, height: 18 }} />}
                                                        minimal
                                                        onClick={() => setQuestionToDelete(q)}
                                                    />

                                                </div>

                                            </div>

                                            {/* OPTIONS */}
                                            {q.value && Object.values(q.value).length > 0 && (
                                                <ul className="question-options">
                                                    {Object.values(q.value).map((opt, i) => (
                                                        <li key={i}>{opt}</li>
                                                    ))}
                                                </ul>
                                            )}

                                        </div>
                                ))}

                            </div>
                        ))}
                </div>

            )}

            <Dialog
                portalContainer={document.body}
                isOpen={isRemarkDialogOpen}
                onClose={() => { handleCancelRemark(); }}
                title={remarkEditingId ? "Edit Comment" : "Add Comment"}
                canEscapeKeyClose={!isRemarkSubmitting}
                canOutsideClickClose={!isRemarkSubmitting}
            >
                <div className="bp3-dialog-body push-20-b push-20-t">
                    <div style={{ fontSize: 14, marginBottom: 8, fontWeight: 600 }}>
                        Title
                    </div>
                    <input
                        autoFocus
                        placeholder="Enter title"
                        disabled={isRemarkSubmitting}
                        value={remarkQuestionTitle}
                        onChange={e => setRemarkQuestionTitle(e.target.value)}
                        style={{
                            width: "100%",
                            border: "1px solid #e1e1e1",
                            boxShadow: "none",
                            borderRadius: 4,
                            padding: "8px 10px"
                        }}
                    />
                </div>


                <Flexbox display="flex" className="bp3-dialog-footer-actions" justifyContent="flex-start" alignItems="center" gap="10px">
                    <FilledButton
                        text="Save"
                        onClick={handleSaveRemark}
                        disabled={isRemarkSubmitting}
                        style={{ marginRight: 8, marginLeft: 20 }}
                    />
                    <TextButton
                        text="Cancel"
                        onClick={() => {
                            if (isRemarkSubmitting) return;
                            handleCancelRemark();
                        }}
                        disabled={isRemarkSubmitting}
                    />
                    {isRemarkSubmitting && <img src="https://s01.live2support.com/dashboardv2/chatassets/img/loader_new.gif" width={35} height={35} alt="Saving..." role="status" />}
                </Flexbox>

            </Dialog>
            {questionToDelete && <QuestionDeleteConfirmation
                question={questionToDelete}
                onClose={() => setQuestionToDelete(null)}
                onDelete={onDeleteQuestion}
                onDeleted={onRefresh}
            />}
            <ConfirmationDialog
                isOpen={isDeleteRemarkDialogOpen}
                onClose={closeDeleteRemarkDialog}
                onConfirm={handleDeleteRemark}
                title="Delete Comment"
                message="Are you sure you want to delete "
                itemName={remarkToDelete?.question || "this comment"}
                confirmText="Delete"
                confirmIntent="danger"
                isSubmitting={isDeleteRemarkSubmitting}
                submittingText="Deleting..."
                errorMessage={deleteRemarkError}
                loadingIndicator={<img src="https://s01.live2support.com/dashboardv2/chatassets/img/loader_new.gif" width={35} height={35} alt="Deleting..." role="status" />}
            />

        </div>
    );
};

export default QuestionListPanel;